import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { Price } from '@adornis/payments/db/prices.js';
import { refundPayment } from '@adornis/payments/server/payment.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { BHOrderNr, BHPayment } from '../../entities/BHPayment.js';
import { Participation } from '../../entities/Participation.js';
import { ParticipationEinzelberatung } from '../../entities/ParticipationEinzelberatung.js';
import { Product } from '../../entities/products.js';
import { RegistrationData } from '../../entities/RegistrationDataBuffer.js';
import { UserStammdaten } from '../../entities/stammdaten.js';
import { User } from '../../entities/user.js';
import { notifyBestaetigungWorkshopRegistrierung } from '../../notification/bestaetigung-workshop-registrierung.js';
import { createPaymentFromParticipations } from './helper.js';

const getProductPrice = async (productID: string, userID: string) => {
  const product = await getByID(Product, productID)({ prices: { _id: 1, unitAmount: 1, ...Price.allFields } });
  if (!product) throw new Error('could not find product ' + productID);
  return product?.prices?.[0] as Maybe<Price>;
};

const paymentPluginDefaultCreatePayment = registerMutation({
  type: () => String,
  operationName: 'paymentPluginDefaultCreatePayment',
  params: [
    { type: () => String, name: 'institutionID' },
    { type: () => [Participation], name: 'participationIds' },
    { type: () => String, name: 'paymentMethods' },
  ],
  resolve: (
    institutionID: string,
    _participations: NarrowedEntity<Participation, { _id: 1; productID: 1 }>[],
    paymentMethod: 'card' | 'paypal',
  ) => {
    return async () => {
      const participations = await getByID(
        Participation,
        _participations.map(pa => pa._id),
      )({
        _id: 1,
        userID: 1,
        user: {
          _id: 1,
          email: 1,
          firstName: 1,
          lastName: 1,
          title: 1,
          username: 1,
          isLdapAccount: 1,
          whatsappNotification: 1,
          whatsappPhone: 1,
          suspentions: { _id: 1, institutionID: 1 },
          receivesNewsletter: 1,
        },
        productID: 1,
        product: {
          _id: 1,
          name: 1,
          institutionID: 1,
          hasPrice: 1,
          prices: { unitAmount: 1, currency: 1 },
        },
        createdAt: 1,
        updatedAt: 1,
        state: 1,
        isNoShow: 1,
        signoffDate: 1,
        registrationDate: 1,
        fulfilledParticipationRequirements: 1,
        lastStateChangeWasInitiatedByAdmin: 1,
        currentPayment: {
          status: 1,
          participationIDs: 1,
          orderNr: { year: 1, nr: 1 },
          amount: 1,
          discount: 1,
        },
        productPriceForUser: { unitAmount: 1, currency: 1 },
      });

      const user = await CurrentUserInfo.getMyself<User>()({ email: 1, firstName: 1, lastName: 1, title: 1 });
      if (!user) throw new Error('user should be loggedIn');

      // create Payment
      const stammdaten = await UserStammdaten.getByUserAndInstitution(
        user._id,
        institutionID,
      )(UserStammdaten.allFields);
      if (!stammdaten) throw new Error('stammdaten not found');
      const payment = await createPaymentFromParticipations(institutionID, participations, getProductPrice);
      payment.orderNr = await BHOrderNr.create(institutionID);
      payment.userReference.userID = user._id;

      const paymentID = await payment.create();
      logger.info({ paymentID }, 'payment created');

      return paymentID;
    };
  },
});

const paymentPluginDefaultGetPaymentLink = registerMutation({
  type: () => String,
  operationName: 'paymentPluginDefaultGetPaymentLink',
  params: [
    { type: () => String, name: 'paymentID' },
    { type: () => String, name: 'paymentMethod' },
  ],
  resolve: (paymentID: string, paymentMethod: 'card' | 'paypal') => {
    return async () => {
      // FIXME: this triggers the creation of another BHPayment (only one payment should be created)
      const paymentLink = RegistrationData.generatePaymentLinkForBooking(paymentID, [paymentMethod]);
      return paymentLink;
    };
  },
});

export const defaultPaymentPlugin = {
  id: 'payment::default',
  type: 'payment',
  paymentMethods: ['card', 'paypal'],
  getProductPrice,
  createPayment: async (participationIDs: string[], paymentMethod) => {
    const user = CurrentUserInfo.getMyself()({ _id: 1, email: 1, firstName: 1, lastName: 1, title: 1 });
    if (!user) throw new Error('user not found');

    const participations = await getByID(
      Participation,
      participationIDs,
    )({
      _id: 1,
      userID: 1,
      user: {
        _id: 1,
        email: 1,
        firstName: 1,
        lastName: 1,
        title: 1,
        username: 1,
        isLdapAccount: 1,
        whatsappNotification: 1,
        whatsappPhone: 1,
        suspentions: { _id: 1, institutionID: 1 },
        receivesNewsletter: 1,
      },
      productID: 1,
      product: {
        _id: 1,
        name: 1,
        institutionID: 1,
        hasPrice: 1,
        prices: { unitAmount: 1, currency: 1 },
      },
      createdAt: 1,
      updatedAt: 1,
      state: 1,
      isNoShow: 1,
      signoffDate: 1,
      registrationDate: 1,
      fulfilledParticipationRequirements: 1,
      lastStateChangeWasInitiatedByAdmin: 1,
      currentPayment: {
        status: 1,
        participationIDs: 1,
        orderNr: { year: 1, nr: 1 },
        amount: 1,
        discount: 1,
      },
      productPriceForUser: { unitAmount: 1, currency: 1 },
    });

    const participationsEinzelberatung = await getByID(
      ParticipationEinzelberatung,
      participationIDs,
    )({
      _id: 1,
      userID: 1,
      user: {
        _id: 1,
        email: 1,
        firstName: 1,
        lastName: 1,
        title: 1,
        username: 1,
        isLdapAccount: 1,
        whatsappNotification: 1,
        whatsappPhone: 1,
        suspentions: { _id: 1, institutionID: 1 },
        receivesNewsletter: 1,
      },
      productID: 1,
      product: {
        _id: 1,
        name: 1,
        institutionID: 1,
        hasPrice: 1,
        prices: { unitAmount: 1, currency: 1 },
      },
      createdAt: 1,
      updatedAt: 1,
      state: 1,
      isNoShow: 1,
      signoffDate: 1,
      registrationDate: 1,
      fulfilledParticipationRequirements: 1,
      lastStateChangeWasInitiatedByAdmin: 1,
      currentPayment: {
        status: 1,
        participationIDs: 1,
        orderNr: { year: 1, nr: 1 },
        amount: 1,
        discount: 1,
      },
      productPriceForUser: { unitAmount: 1, currency: 1 },
    });

    const userOwnedProductIDs = await BHPayment.getUserProductsForWhichAPaymentExists()();
    const newParticipations = [...participations, ...participationsEinzelberatung].filter(
      pa => !userOwnedProductIDs.includes(pa.productID as string),
    );
    if (newParticipations.length === 0) {
      throw new Error('no participations found to create a payment with');
    }

    const institutionID = newParticipations[0]?.product?.institutionID;
    if (!institutionID) throw new Error('something went wrong');

    const paymentID = await paymentPluginDefaultCreatePayment(institutionID, newParticipations, paymentMethod)();
    return [paymentID];
  },

  pay: async (paymentID, paymentMethod) => {
    const paymentLink = await paymentPluginDefaultGetPaymentLink(paymentID, paymentMethod)();
    if (!paymentLink) throw new Error('could not create payment Link');

    await goTo(paymentLink, { target: '_self' });
  },

  afterEnrollment: async participationID => {},

  cancleEnrollment: async (participationID: string) => {
    const participation = getByID(Participation, participationID)({ currentPayment: BHPayment.allFields });
    if (!participation.currentPayment) return;
    const payment = participation.currentPayment;
    switch (payment.status) {
      case 'paid':
        await refundPayment({ paymentInstance: payment, reason: 'requested_by_customer' });
        return;
      case 'created':
      case 'issued':
        payment.status = 'canceled';
        await payment.save();
      default:
        throw new Error('handling of paymentState=' + payment.status + ' is not implemented');
    }
  },

  sendInvoice: async (paymentID: string) => {},
  onPaymentUpdate: async (institutionID, paymentOld, paymentNew) => {
    if (!paymentOld) return;
    const statusChanged = paymentOld.status !== paymentNew.status;

    if (statusChanged && paymentNew.status === 'paid') {
      const participations = await Participation.getByID<Participation>(paymentNew.participationIDs)({
        _id: 1,
        userID: 1,
        user: {
          _id: 1,
          email: 1,
          firstName: 1,
          lastName: 1,
          title: 1,
          username: 1,
          isLdapAccount: 1,
          whatsappNotification: 1,
          whatsappPhone: 1,
          suspentions: { _id: 1, institutionID: 1 },
          receivesNewsletter: 1,
        },
        productID: 1,
        product: {
          _id: 1,
          name: 1,
          institutionID: 1,
          hasPrice: 1,
          prices: { unitAmount: 1, currency: 1 },
        },
        createdAt: 1,
        updatedAt: 1,
        state: 1,
        isNoShow: 1,
        signoffDate: 1,
        registrationDate: 1,
        fulfilledParticipationRequirements: 1,
        lastStateChangeWasInitiatedByAdmin: 1,
        currentPayment: {
          status: 1,
          participationIDs: 1,
          orderNr: { year: 1, nr: 1 },
          amount: 1,
          discount: 1,
        },
        productPriceForUser: { unitAmount: 1, currency: 1 },
      });

      const p1 = participations[0];
      if (!p1) return;
      const user = await getByID(
        User,
        p1.userID as string,
      )({
        _id: 1,
        email: 1,
        firstName: 1,
        lastName: 1,
        title: 1,
        username: 1,
        isLdapAccount: 1,
        whatsappNotification: 1,
        whatsappPhone: 1,
        suspentions: { _id: 1, institutionID: 1 },
        receivesNewsletter: 1,
      });
      if (!user) throw new Error('user not found');
      await notifyBestaetigungWorkshopRegistrierung(institutionID, user, participations);
    }
  },
  priceGroups: ['default'],
} satisfies Paymentlugin<'card' | 'paypal', 'default'>;
